import React, { memo, forwardRef, useState } from 'react';
import type { RefAttributes, CSSProperties } from 'react';
import clsx from 'clsx';

import Cell from './Cell';
import EditCell from './EditCell';

import type { RowRendererProps } from './types';
import { RowSelectionProvider, useLatestFunc } from './hooks';
import { getColSpan } from './utils';
import { groupRowSelectedClassname, rowClassname, rowReorderTargetClassname } from './style';

function Row<R, SR>(
  {
    cellMouseDownHandler,
    cellStyles,
    className,
    draggedOverRange,
    dragPos,
    enableDrag,
    height,
    isRowSelected,
    lastFrozenColumnIndex,
    onMouseEnter,
    onMouseLeave,
    onMouseUp,
    onRowClick,
    onRowDoubleClick,
    onRowChange,
    reorderRows,
    row,
    rowClass,
    rowIdx,
    selectCell,
    selectedCellProps,
    selectedRange,
    top,
    updateDraggedOverRange,
    viewportColumns,
    ...props
  }: RowRendererProps<R, SR>,
  ref: React.Ref<HTMLDivElement>
) {
  const [dragHover, setDragHover] = useState<boolean>(false);
  const isReordering = dragPos.current?.selType === "REORDER" && dragHover;

  function handleDragEnter(event: React.MouseEvent<HTMLDivElement>) {
    setDragHover(true);
    onMouseEnter?.(event);
  }

  function handleDragLeave(event: React.MouseEvent<HTMLDivElement>) {
    setDragHover(false);
    onMouseLeave?.(event);
  }

  function handleMouseUp(event: React.MouseEvent<HTMLDivElement>) {
    setDragHover(false);
    onMouseUp?.(event);
  }

  const handleRowChange = useLatestFunc((newRow: R) => {
    onRowChange(rowIdx, newRow);
  });

  className = clsx(
    rowClassname,
    `rdg-row-${rowIdx % 2 === 0 ? 'even' : 'odd'}`, {
    [groupRowSelectedClassname]: selectedCellProps?.idx === -1,
    'rdg-row-selected': isRowSelected,
    [rowReorderTargetClassname]: isReordering
  },
    rowClass?.(row),
    className
  );

  const cells = [];
  for (let index = 0; index < viewportColumns.length; index++) {
    const column = viewportColumns[index];
    const { idx } = column;
    const colSpan = getColSpan(column, lastFrozenColumnIndex, { type: 'ROW', row });
    if (colSpan !== undefined) {
      index += colSpan - 1;
    }

    const isCellFocused = !!(selectedCellProps && selectedCellProps.idx === idx);
    const isCellSelected = !isCellFocused && !!(selectedRange && selectedRange[0] <= idx && selectedRange[1] >= idx);
    const isDraggedOver = !!(draggedOverRange && draggedOverRange[0] <= idx && draggedOverRange[1] >= idx);

    let dragHandleProps;
    let onFocus;
    let onKeyDown;

    if (selectedCellProps !== undefined && isCellSelected && selectedCellProps.mode === 'SELECT') {
      ({ dragHandleProps, onFocus, onKeyDown } = selectedCellProps);
    }

    if (isCellFocused && selectedCellProps?.mode === 'EDIT') {
      cells.push(
        <EditCell
          key={column.key}
          column={column}
          colSpan={colSpan}
          onKeyDown={(selectedCellProps).onKeyDown}
          {...(selectedCellProps).editorProps}
        />
      );
    } else {
      cells.push(
        <Cell
          cellMouseDownHandler={cellMouseDownHandler}
          cellStyles={cellStyles?.[column.idx]}
          colSpan={colSpan}
          column={column}
          dragHandleProps={dragHandleProps}
          dragPos={dragPos}
          enableDrag={isCellFocused ? enableDrag : false}
          isDraggedOver={isDraggedOver}
          isCellFocused={isCellFocused}
          isCellSelected={isCellSelected}
          isRowSelected={isRowSelected}
          isReorderingRow={isReordering}
          key={column.key}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onMouseEnter={handleDragEnter}
          onMouseLeave={handleDragLeave}
          onMouseUp={handleMouseUp}
          onRowClick={onRowClick}
          onRowDoubleClick={onRowDoubleClick}
          onRowChange={handleRowChange}
          reorderRows={reorderRows}
          row={row}
          rowIdx={rowIdx}
          selectCell={selectCell}
          updateDraggedOverRange={updateDraggedOverRange}
        />
      );
    }
  }

  return (
    <RowSelectionProvider value={isRowSelected}>
      <div
        role="row"
        ref={ref}
        className={className}
        style={
          {
            top,
            '--row-height': `${height}px`
          } as unknown as CSSProperties
        }
        {...props}
      >
        {cells}
      </div>
    </RowSelectionProvider>
  );
}

export default memo(forwardRef(Row)) as <R, SR>(
  props: RowRendererProps<R, SR> & RefAttributes<HTMLDivElement>
) => JSX.Element;
